import { Dispatch, SetStateAction, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Grid, Paper, TextField, Typography, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { tokens } from '../../theme';
import { api, apiAuthentication, tokenKey, ILoginRequest, ILoginResponse } from '../../types';
import { createApiHeaders } from '../../utilities/JwtTokenParser';
import { ReactComponent as CompanyLogo } from '../../resources/Logo-800.svg';

interface MainLoginProps {
  setLoggedIn: Dispatch<SetStateAction<boolean>>;
}

const loginRequestPrototype : ILoginRequest = {
  login: "",
  password: "",
}

const createToken = async (loginRequest: ILoginRequest): Promise<ILoginResponse> => {
  const data = await fetch(`${api}${apiAuthentication}token`, {
    method: 'POST',
    headers: createApiHeaders(false, false),
    body: JSON.stringify(loginRequest)
  });

  const json = await data.json();
  return json;
}

const validationSchema = yup.object().shape({
  login: yup.string().required("required"),
  password: yup.string().required("required")
});

const MainLogin = ({ setLoggedIn }: MainLoginProps) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loginRequest] = useState<ILoginRequest>(loginRequestPrototype);
  const [error, setError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: loginRequest,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      createToken(values)
      .then((response) => {
        if (response.token) {
          sessionStorage.setItem(tokenKey, response.token);
          setError(null);
          setLoggedIn(true);
          navigate("/dashboard");
        } else {
          setError(response.error);
          setLoggedIn(false);
        }
      })
      .catch((error) => {
        setError(error);
        setLoggedIn(false);
      });
    }
  });
  
  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: "100vh"
      }}
    >
      <Grid item xs={3}>
        <Paper
          variant="outlined" 
          sx={{
            padding: "14px",
            minWidth: "400px"
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            <Box 
              display="flex" 
              justifyContent="center" 
              mb="28px"
            >
              <CompanyLogo />
            </Box>

            <Box
              display="grid"
              gridTemplateColumns="repeat(12, minmax(0, 1fr))"
              gap="20px"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 12" }
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                id="login"
                name="login"
                label="Login"
                color="info"
                value={formik.values.login}
                onChange={formik.handleChange} 
                onBlur={formik.handleBlur}
                error={formik.touched.login && Boolean(formik.errors.login)}
                helperText={formik.touched.login && formik.errors.login}
                sx={{ gridColumn: "span 12" }}
              />
              
              <TextField
                fullWidth
                variant="filled"
                id="password"
                name="password"
                label="Password"
                color="info"
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                sx={{ gridColumn: "span 12" }}
              />
              
              <Button 
                type="submit" 
                variant="contained" 
                color="secondary"
                sx={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "8px 14px",
                  gridColumn: "span 12"
                }}
              >
                LOG IN
              </Button>
            </Box>
            {
              error && 
              <Box mt="8px">
                <Typography 
                  variant="caption" 
                  color={colors.redAccent[700]}
                  fontWeight="bold"
                > 
                  {error}
                </Typography>
              </Box>
            }

            <Box mt="28px">
              <Typography 
                variant="caption" 
                color={colors.grey[600]}
                fontWeight="bold"
              > 
                &copy; {(new Date()).getFullYear()} Fillware Technologies. All Rights Reserved.
              </Typography>
            </Box>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default MainLogin;